import React, { memo, useMemo } from "react";
import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import CodeBlock from "../CodeBlock/CodeBlock";
import styles from "./Answer.module.css";

interface MarkdownRendererProps {
  decodedContent: string;
  isStreaming: boolean;
  compact?: boolean;
}

interface RenderCodeProps {
  inline?: boolean;
  className?: string;
  children?: React.ReactNode;
  [key: string]: any;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = memo(
  ({ decodedContent, isStreaming, compact = false }) => {
    // Base components — rebuilt when isStreaming changes
    const baseComponents: Components = useMemo(
      () => ({
        code: ({ inline, className, children, ...props }: RenderCodeProps) => {
          const languageMatch = /language-(\w+)/.exec(className || "");

          // If it's inline code (no language or explicitly marked inline)
          if (inline || !languageMatch) {
            return (
              <code className={styles.inlineCode} {...props}>
                {children}
              </code>
            );
          }

          // Otherwise render as syntax-highlighted code block
          const language = languageMatch[1];
          return (
            <CodeBlock
              className={`language-${language}`}
              isStreaming={isStreaming}
            >
              {String(children).trim()}
            </CodeBlock>
          );
        },
        table: (props) => (
          <div className={styles.tableWrapper}>
            <table className={styles.table} {...props} />
          </div>
        ),
        thead: (props) => <thead className={styles.tableHead} {...props} />,
        tbody: (props) => <tbody className={styles.tableBody} {...props} />,
        tr: (props) => <tr className={styles.tableRow} {...props} />,
        th: (props) => <th className={styles.tableHeaderCell} {...props} />,
        td: (props) => <td className={styles.tableCell} {...props} />,
      }),
      [isStreaming],
    );

    const compactOverrides: Components = useMemo(
      () =>
        compact
          ? {
              h1: (props) => (
                <h4 {...props} className={styles.compactHeading} />
              ),
              h2: (props) => (
                <h5 {...props} className={styles.compactHeading} />
              ),
              h3: (props) => (
                <h6 {...props} className={styles.compactHeading} />
              ),
              p: (props) => (
                <p {...props} className={styles.compactParagraph} />
              ),
            }
          : {},
      [compact],
    );

    const components = useMemo(
      () => ({ ...baseComponents, ...compactOverrides }),
      [baseComponents, compactOverrides],
    );

    return (
      <div style={{ flexGrow: 1 }}>
        <div
          className={`${styles.answerText} ${compact ? styles.compact : ""}`}
        >
          <ReactMarkdown
            children={decodedContent}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={components}
          />
        </div>
      </div>
    );
  },
);

export default MarkdownRenderer;
